import { useCallback, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { ValuesMap } from '@/common/types';
import { API_URL } from '@/dtos';

import { IPermission, IPermissionGroup, IRawPermissionResponse, IRoleSetup, RoleLevel } from '../role.types';

export const QUERY_KEY = 'GET_ROLE_BY_ID';
interface Params {
  id: string;
}

interface Response {
  description: string;
  id: number;
  level_type: RoleLevel;
  name: string;
  organization_id?: number;
  role_ids_allowed_for_creation: number[];
  allowed_for_creation_by_role_ids: number[];
  granted_permissions: IRawPermissionResponse[];
  not_granted_permissions: IRawPermissionResponse[];
  org_properties_state_config_id?: number;
}

const useGetRoleByIdQuery = ({ id }: Params) => {
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY, id],
    // TODO: the type is missing
    () => api.get<Response>(API_URL.ROLES_BY_ID(id)),
    {
      enabled: !!id,
    }
  );
  const getPermissionGroups = useCallback((values: IRawPermissionResponse[]) => {
    const groups: IPermissionGroup[] = [];
    const groupsKeys: number[] = [];
    const valuesMap: ValuesMap<IPermission[]> = {};
    const groupsMap: ValuesMap<string> = {};

    values.forEach((value) => {
      const item: IPermission = {
        id: `${value.id || 0}`,
        key: `${value.id || 0}`,
        name: value.display_name || value.name,
        description: value.description,
        groupName: value.group_name,
        group: value.group_id,
      };
      if (!valuesMap[value.group_id]) {
        groupsKeys.push(value.group_id);
        valuesMap[value.group_id] = [item];
        groupsMap[value.group_id] = value.group_name;
      } else {
        valuesMap[value.group_id] = [...valuesMap[value.group_id], item];
      }
    });
    groupsKeys.forEach((key) => {
      groups.push({
        id: `${key}`,
        name: groupsMap[key] || `${key}`,
        options: valuesMap[key],
      });
    });

    return groups;
  }, []);
  const formattedData: IRoleSetup | null = useMemo(() => {
    if (!data?.data) {
      return null;
    }
    const {
      id,
      name,
      level_type,
      granted_permissions = [],
      not_granted_permissions = [],
      description,
      organization_id,
      allowed_for_creation_by_role_ids = [],
      role_ids_allowed_for_creation = [],
      org_properties_state_config_id,
    } = data?.data || {};

    return {
      id: `${id || ''}`,
      name: name,
      permissions: [],
      level: level_type,
      description: description,
      fieldLevelAccessId: `${org_properties_state_config_id || ''}`,
      organizationId: !!organization_id || organization_id === 0 ? `${organization_id}` : undefined,
      rolesIdsAllowedForCreation: allowed_for_creation_by_role_ids.map((item) => `${item}`),
      isAllowedToCreateAnotherRole: allowed_for_creation_by_role_ids.length > 0,
      allowedForContactsByRoleId: role_ids_allowed_for_creation?.map((item) => `${item}`) || [],
      granted: getPermissionGroups(granted_permissions),
      revoked: getPermissionGroups(not_granted_permissions),
    };
  }, [data?.data, getPermissionGroups]);
  return {
    isLoading,
    data: formattedData,
    isSuccess,
  };
};

export default useGetRoleByIdQuery;
